import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import {
  HiOutlineArrowDownTray,
  HiOutlineDocumentPlus,
  HiOutlineEye,
  HiOutlinePencilSquare,
} from "react-icons/hi2";

import { isApiClientError } from "../api/errors";
import { useDocumentFiles } from "./documentFilesContext";
import { ScanStatusBadge, SourceBadge } from "./FileBadges";
import { scanStatusView } from "./fileLabels";
import { FileTypeIcon } from "./FileTypeIcon";
import { CommentSource, EmailSource } from "./FileProvenance";
import { SignedPreviewDialog } from "./FilePreviewDialog";
import { fileTypeLabel, formatBytes } from "./formatting";
import { unavailableReasons } from "./signingMessages";
import type { SourceFile } from "./useDocumentFileSources";
import { isGlobalReason } from "./useSigningAvailability";
import { signingAvailability } from "../entities/document-workflow";
import { downloadErrorMessage, downloadSignedFile } from "./versionDownload";
import { Notice } from "./Notice";

function linkErrorMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.code === "document_workflow.quarantined_file") {
      return "Файл не прошёл проверку или ещё проверяется, поэтому добавить его в файлы документа нельзя.";
    }
    if (error.status === 403) {
      return "Недостаточно прав, чтобы добавить файл в файлы документа.";
    }
    if (error.status === 404) {
      return "Источник файла не найден или больше не доступен. Обновите карточку.";
    }
    if (error.status === 503) {
      return "Хранилище временно недоступно. Повторите попытку позже.";
    }
  }

  return "Не удалось добавить файл в файлы документа. Повторите попытку.";
}

/**
 * Файл из переписки или обсуждения, ещё не занесённый в файлы документа.
 *
 * Показывается наравне с остальными файлами — с источником, состоянием
 * проверки и теми же действиями. Добавление в файлы документа не копирует
 * байты: создаётся первая версия, ссылающаяся на тот же объект хранилища, и
 * только после этого файл можно подписывать и вести его версии.
 */
export function SourceFileCard({ source }: { source: SourceFile }) {
  const {
    services,
    documentId,
    canUnlink: canLink,
    canSign,
    signatureCapabilities,
    signingNoticeId,
    requestSigning,
  } = useDocumentFiles();
  const client = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);
  const hintId = useId();
  const scan = scanStatusView(source.scanStatus);

  const file = { scan_status: source.scanStatus, sha256: source.sha256 };
  const download = useMutation({
    mutationFn: () =>
      downloadSignedFile(
        () =>
          source.attachmentId
            ? services.documentWorkflow.downloadMailAttachment(
                source.attachmentId,
              )
            : services.files.download(source.blobId).then(({ data }) => data),
        file,
        source.name,
      ),
  });
  const link = useMutation({
    mutationFn: (signAfter: boolean) =>
      (source.attachmentId
        ? services.documentWorkflow.linkMailAttachment(
            documentId,
            source.attachmentId,
            { displayName: source.name },
          )
        : services.documentWorkflow.linkFile(
            documentId,
            source.blobId,
            source.name,
          )
      ).then((version) => ({ version, signAfter })),
    onSuccess: async ({ version, signAfter }) => {
      if (signAfter) requestSigning(version.id);
      await client.invalidateQueries({ queryKey: ["document-workflow"] });
    },
  });

  const availability = signingAvailability({
    capabilities: signatureCapabilities,
    canSign,
    browserProfiles: services.cryptoPro?.supportedProfiles ?? [],
    version: file,
  });
  const globalBlock = isGlobalReason(availability);
  const hint = !canLink
    ? "Чтобы подписать файл или вести его версии, его нужно добавить в файлы документа — для этого нужно право связывать файлы."
    : availability.status === "unavailable" &&
        availability.reason === "version_not_ready"
      ? unavailableReasons.version_not_ready
      : null;
  const canSignNow = canLink && availability.status === "available";
  const previewLoader = () =>
    source.attachmentId
      ? services.documentWorkflow.previewMailAttachment(source.attachmentId)
      : services.files.preview(source.blobId).then(({ data }) => data);

  return (
    <li className="file-card file-card--source" data-source={source.origin}>
      <div className="file-card__head">
        <FileTypeIcon mimeType={source.mimeType} />
        <div className="file-card__title">
          <strong className="file-card__name">{source.name}</strong>
          <p className="file-card__meta">
            {`${fileTypeLabel(source.mimeType)} · ${formatBytes(source.size ?? 0)}`}
          </p>
        </div>
      </div>
      <div className="file-card__badges">
        <ScanStatusBadge status={source.scanStatus} />
        <span className="file-card__state">Пока не в файлах документа</span>
      </div>
      <div className="file-provenance">
        <SourceBadge type={source.origin} />
        {source.messageId ? <EmailSource messageId={source.messageId} /> : null}
        {source.commentId ? (
          <CommentSource commentId={source.commentId} />
        ) : null}
      </div>
      <div className="file-actions">
        {scan.releasable ? (
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => setPreviewOpen(true)}
          >
            <HiOutlineEye aria-hidden="true" />
            <span>Открыть</span>
          </button>
        ) : null}
        {scan.releasable ? (
          <button
            className="button button--secondary button--compact"
            type="button"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            <HiOutlineArrowDownTray aria-hidden="true" />
            <span>Скачать</span>
          </button>
        ) : null}
        <button
          className="button button--primary button--compact"
          type="button"
          disabled={!canSignNow || link.isPending}
          aria-describedby={
            globalBlock ? signingNoticeId : hint ? hintId : undefined
          }
          aria-label={`Подписать ${source.name}`}
          onClick={() => link.mutate(true)}
        >
          <HiOutlinePencilSquare aria-hidden="true" />
          <span>Подписать</span>
        </button>
        <button
          className="button button--text button--compact"
          type="button"
          disabled={!canLink || !scan.releasable || link.isPending}
          aria-label={`Добавить в файлы документа: ${source.name}`}
          onClick={() => link.mutate(false)}
        >
          <HiOutlineDocumentPlus aria-hidden="true" />
          <span>Добавить в файлы</span>
        </button>
      </div>
      {!scan.releasable ? (
        <p className="file-hint" data-blocked={source.scanStatus}>
          {scan.blockedReason}
        </p>
      ) : (
        <p className="file-hint">
          Чтобы подписать файл, он добавляется в файлы документа без
          копирования: создаётся версия 1, ссылающаяся на тот же файл.
        </p>
      )}
      {hint ? (
        <p id={hintId} className="file-hint">
          {hint}
        </p>
      ) : null}
      {link.isPending ? (
        <p role="status" className="file-hint">
          Добавляем файл в файлы документа…
        </p>
      ) : null}
      {link.isError ? <Notice>{linkErrorMessage(link.error)}</Notice> : null}
      {download.isError ? (
        <Notice>{downloadErrorMessage(download.error)}</Notice>
      ) : null}
      {previewOpen ? (
        <SignedPreviewDialog
          title={source.name}
          mimeType={source.mimeType ?? "application/octet-stream"}
          loadPreview={previewLoader}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </li>
  );
}
